import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { appConfig } from '../../../appConfig';
import recipeFlow from '../../recipe/flow/recipeFlow';
import { savedFavoriteRecipeKey } from '../../recipe/recipeQueries';
import favouriteRecipesAnonymousCookie from '../persistance/cookie/favouriteRecipesAnonymous';
import { ErrorGroup } from '../store/structureDefinitions/errorsState';
import { ga4RecipesTracking } from '../tracking/ga4/recipes';
import { useGroupErrorEffect } from './useGroupError';

export const fetchFavoriteRecipeIds = async () => {
    let favoriteExternalRecipeIds: string[];
    if (appConfig.coopUserSettings.isAuthenticated) {
        const favoriteRecipes = await recipeFlow.getFavouriteRecipes();
        favoriteExternalRecipeIds =
            favoriteRecipes?.map((i) => i.externalRecipeId.toString()) || [];
    } else {
        favoriteExternalRecipeIds = favouriteRecipesAnonymousCookie.get();
    }
    return favoriteExternalRecipeIds;
};

export const useFavoriteRecipeIds = (enabled: boolean = true) => {
    const query = useQuery({
        queryKey: savedFavoriteRecipeKey,
        queryFn: fetchFavoriteRecipeIds,
        staleTime: 5000,
        gcTime: 30000,
        enabled,
    });
    return query;
};

/**
 * Hook for a single teaser.Must be injected with "all favorite recipes" data, either by useFavouriteRecipes or FavoriteRecipeContext
 */

export const useFavouriteSingleRecipe = (
    externalRecipeId: string,
    onSaveSuccess: () => void,
    onRemoveSuccess: () => void,
) => {
    const { data: favouriteRecipeExternalIds, isLoading, isError } = useFavoriteRecipeIds();
    useGroupErrorEffect(ErrorGroup.Global, isError);
    const queryClient = useQueryClient();

    const save = useMutation({
        mutationFn: (id: string) => {
            if (appConfig.coopUserSettings.isAuthenticated) {
                return saveRecipes(id);
            }
            favouriteRecipesAnonymousCookie.add(externalRecipeId);
            return Promise.resolve(true);
        },
        onSuccess: () => {
            onSaveSuccess?.();
            queryClient.invalidateQueries({ queryKey: savedFavoriteRecipeKey });
        },
    });
    useGroupErrorEffect(ErrorGroup.Global, save.isError);

    const remove = useMutation({
        mutationFn: (id: string) => {
            if (appConfig.coopUserSettings.isAuthenticated) {
                return removeRecipes(id);
            }
            favouriteRecipesAnonymousCookie.remove(externalRecipeId);
            return Promise.resolve(true);
        },
        onSuccess: () => {
            onRemoveSuccess?.();
            queryClient.invalidateQueries({ queryKey: savedFavoriteRecipeKey });
        },
    });

    useGroupErrorEffect(ErrorGroup.Global, remove.isError);

    const isFavourite = !!favouriteRecipeExternalIds?.find((id) => id === externalRecipeId);
    const saveRecipes = async (_externalRecipeId: string) => {
        const res = await recipeFlow.saveFavouriteRecipe(_externalRecipeId);
        return res;
    };

    const removeRecipes = async (_externalRecipeId: string) => {
        const res = await recipeFlow.removeFavouriteRecipe(_externalRecipeId);
        return res;
    };

    const saveFavouriteRecipe = () => {
        save.mutate(externalRecipeId);
    };

    const removeFavouriteRecipe = () => {
        remove.mutate(externalRecipeId);
    };

    const onFavouriteRecipeToggleClick = () => {
        if (isFavourite) {
            removeFavouriteRecipe();
        } else {
            saveFavouriteRecipe();
        }
    };

    return {
        isFavourite,
        onFavouriteRecipeToggleClick,
        save,
        remove,
        isLoading,
    };
};

export const useSingleFavorite = (
    id: string,
    name: string,
    onSaveSuccess: () => void,
    onRemoveSuccess: () => void,
) => {
    const {
        isFavourite,
        onFavouriteRecipeToggleClick,
        remove: removeResult,
        save: saveResult,
        isLoading,
    } = useFavouriteSingleRecipe(id, onSaveSuccess, onRemoveSuccess);

    useEffect(() => {
        if (removeResult.isSuccess && !isFavourite) {
            ga4RecipesTracking.favoriteRecipe.remove(id, name);
        }
    }, [removeResult.isSuccess, isFavourite, id, name]);

    useEffect(() => {
        if (saveResult.isSuccess && isFavourite) {
            ga4RecipesTracking.favoriteRecipe.add(id, name);
        }
    }, [saveResult.isSuccess, isFavourite, id, name]);

    return {
        isFavourite,
        onFavouriteRecipeToggleClick,
        isLoading,
    };
};
